import { useCallback, useMemo } from 'react';

import {
  ListValue,
  useItemsSearch,
  useKeyboardNavigation,
} from '@xemplo/stateful-list-core';

import { useValueTracker } from '../../utils/hooks/use-value-tracker';
import { MultiSelectContextType } from '../../utils/types/stateful-list.types';

import { MultiSelectInputProps } from './multi-select.types';

export function useMultiState(props: MultiSelectInputProps) {
  const { items, onChange, useSearch = true, useSelectAll = true, value } = props;
  const { renderItems, valueItems, valueItemMap, isNoResult, search, setSearch } =
    useItemsSearch({ items });

  const { selectedItems, isSelected, toggleItem, updateSelection } = useValueTracker({
    value,
    onChange,
  });

  const isAllSelected = valueItems.every((item) => selectedItems.has(item.value));
  const isAnySelected = valueItems.some((item) => selectedItems.has(item.value));

  const handleToggleItem = useCallback(
    (value: ListValue) => toggleItem(value, valueItemMap),
    [toggleItem, valueItemMap]
  );

  const selectAll = useCallback(() => {
    const newSet = new Set(selectedItems);
    if (isAllSelected) {
      valueItems.forEach((item) => newSet.delete(item.value));
    } else {
      valueItems.forEach((item) => newSet.add(item.value));
    }

    updateSelection(newSet);
  }, [isAllSelected, selectedItems, valueItems, updateSelection]);

  // Patch valueItems to include the itemCallback for toggling selection
  // This allows each item to call toggleItem when clicked
  // or selected via keyboard navigation
  const patchedValueItems = useMemo(
    () => valueItems.map((item) => ({ ...item, itemCallback: handleToggleItem })),
    [handleToggleItem, valueItems]
  );

  const { activeDescendantId, containerRef, onKeyDown } = useKeyboardNavigation({
    id: renderItems.id,
    items: patchedValueItems,
    useSearch,
    useSelectAll,
    selectAllCallback: selectAll,
  });

  return useMemo(
    () =>
      ({
        items,
        valueItems,
        isNoResult,
        isAllSelected,
        indeterminate: isAnySelected && !isAllSelected,
        renderItems,
        selectedItems,
        useSearch,
        useSelectAll,
        activeDescendantId,
        containerRef,
        search,
        onKeyDown,
        toggleItem: handleToggleItem,
        isSelected,
        setSearch,
        selectAll,
      } satisfies MultiSelectContextType),
    [
      activeDescendantId,
      containerRef,
      isAllSelected,
      isAnySelected,
      isNoResult,
      isSelected,
      items,
      onKeyDown,
      renderItems,
      search,
      selectAll,
      selectedItems,
      setSearch,
      handleToggleItem,
      useSearch,
      useSelectAll,
      valueItems,
    ]
  );
}
